import { useCallback } from "react";
import { NavigateOptions, useNavigate } from "react-router-dom";

import { assert } from "@rewards-web/shared/lib/assert";

import { useRoutePathPrefix } from "./use-route-path-prefix";

type UseRouteNavigate = {
  (to: string, options?: NavigateOptions): void;
  (delta: number): void;
};

/**
 * A drop-in replacement for `useNavigate` that prefixes the path with the given path prefix.
 *
 * This should be used anytime we are navigating to an authenticated route in the app.
 */
export function useRouteNavigate(opts?: {
  /**
   * If `true`, the path prefix will not be added to the path.
   */
  ignorePathPrefix?: boolean;
}): UseRouteNavigate {
  const navigate = useNavigate();
  const routingPathPrefix = useRoutePathPrefix();

  return useCallback<UseRouteNavigate>(
    (...args) => {
      if (typeof args[0] === "number") {
        navigate(args[0]);
      } else {
        assert(
          typeof args[0] === "string",
          "The first argument must be a string"
        );

        const path = opts?.ignorePathPrefix
          ? args[0]
          : `${routingPathPrefix}${args[0]}`;

        navigate(path, ...(args.slice(1) as any));
      }
    },
    [navigate, routingPathPrefix, opts?.ignorePathPrefix]
  );
}
